import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CheckCircle2, Home, PlusCircle, AlertCircle } from "lucide-react";
import { crearServicio } from "../services/serviciosAsociacionService.ts";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import { SERVICE_ICON_OPTIONS } from "../utils/serviceIcons.ts";

export default function ServicioCrear() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const [nombre, setNombre] = useState("");
  const [categoria, setCategoria] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [icono, setIcono] = useState("auto");
  const [activo, setActivo] = useState(true);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (!isAdmin) return <Navigate to="/inicio" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMensaje(null);
    setError(null);
    if (!nombre.trim()) {
      setError("Ingresá un nombre para el servicio.");
      return;
    }
    try {
      setEnviando(true);
      const token = await getAccessTokenSilently();
      await crearServicio(
        {
          nombre: nombre.trim(),
          categoria: categoria.trim() || undefined,
          descripcion: descripcion.trim() || undefined,
          icono: icono !== "auto" ? icono : undefined,
          activo,
        },
        token
      );
      setMensaje(`Servicio "${nombre.trim()}" creado correctamente.`);
      setNombre("");
      setCategoria("");
      setDescripcion("");
      setIcono("auto");
      setActivo(true);
      setTimeout(() => navigate("/servicios-admin"), 800);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.message || "No se pudo crear el servicio.";
      setError(apiMsg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Nuevo servicio</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 p-6 text-white shadow-2xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-100">Configuración</p>
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">Crear servicio</h1>
              <p className="text-sm text-indigo-50">
                Agregá servicios al catálogo para luego asignarlos a los socios.
              </p>
            </div>
            <div className="rounded-2xl bg-white/15 p-3 text-sm font-semibold text-white shadow-sm backdrop-blur inline-flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Alta de servicio
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
          {mensaje && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {mensaje}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">
                Nombre
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Categoría
                <input
                  type="text"
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Ej: Seguridad, Salud..."
                />
              </label>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">Icono</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {SERVICE_ICON_OPTIONS.map(({ key, label, Icon }) => {
                  const selected = icono === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setIcono(key)}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                        selected
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      aria-pressed={selected}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2} />
                      <span className="text-[11px]">{label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">Elegi un icono para el servicio.</p>
            </div>
            <label className="text-sm font-semibold text-slate-700">
              Descripción
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="mt-1 min-h-[120px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Opcional: breve detalle del servicio."
              />
            </label>
            <div className="flex items-center gap-2">
              <input
                id="activo"
                type="checkbox"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="activo" className="text-sm font-semibold text-slate-700">
                Activo
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={enviando}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
              >
                <PlusCircle className="h-4 w-4" />
                {enviando ? "Guardando..." : "Crear servicio"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
